import React, { useEffect, useState } from "react";
import {
  Alert,
  Avatar,
  Box,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  Link,
  Paper,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PersonIcon from "@mui/icons-material/Person";
import { displayPatientNameFromParts } from "../../utils/displayPatientName";
import { patientAgeYears } from "../../context/normalizePatient";
import { normalizeBase64Image } from "../utils/utils";
import { fetchPatientDemographics, isAbortError } from "./api";
import { PR_COLORS } from "./constants";
import PastAppointmentsSection from "./PastAppointmentsSection";
import CareContextsSection from "./CareContextsSection";
import HealthRecordsAccordion from "./HealthRecordsAccordion";

const NOT_RECORDED = "Not recorded";

/** ABHA absence is stated outright — a blank field sends users hunting. */
const NOT_PRESENT = "No ABHA linked";

const joinAddress = (patient) =>
  [
    patient.address_line,
    patient.city,
    patient.district,
    patient.state,
    patient.pincode,
  ]
    .map((part) => (part == null ? "" : String(part).trim()))
    .filter(Boolean)
    .join(", ");

/** One label/value pair. Read-only by construction — nothing here is an input. */
const Field = ({ label, value, span = 4 }) => (
  <Grid item xs={12} sm={6} md={span}>
    <Typography
      sx={{
        color: PR_COLORS.muted,
        fontSize: "0.72rem",
        textTransform: "uppercase",
        letterSpacing: "0.04em",
        fontWeight: 600,
      }}
    >
      {label}
    </Typography>
    <Typography sx={{ fontSize: "0.92rem", wordBreak: "break-word" }}>
      {value || NOT_RECORDED}
    </Typography>
  </Grid>
);

const SectionTitle = ({ children }) => (
  <Typography
    variant="subtitle1"
    sx={{
      color: PR_COLORS.heading,
      fontWeight: 700,
      display: "flex",
      alignItems: "center",
      gap: 1,
      mb: 1.5,
    }}
  >
    {children}
  </Typography>
);

/**
 * Read-only consolidated picture of one Patient, opened from the Patient List.
 *
 * Shows Local Data only. External retrieval lives in the collapsed accordion at
 * the bottom, behind a deliberate extra step (ADR-0003). Each section below owns
 * its own fetch so one failing read cannot blank the others.
 */
export default function PatientOverviewPane({ rosterPatient, onBack }) {
  const patientId = rosterPatient?.patient_id;

  const [details, setDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [detailsError, setDetailsError] = useState("");

  useEffect(() => {
    if (!patientId) return undefined;

    const controller = new AbortController();

    // Cleared up front so a slower second fetch can never render beside the
    // previous Patient's values.
    setDetails(null);
    setDetailsError("");
    setLoadingDetails(true);

    fetchPatientDemographics({ patientId, signal: controller.signal })
      .then((data) => {
        if (controller.signal.aborted) return;
        setDetails(data);
      })
      .catch((err) => {
        if (isAbortError(err)) return;
        console.error("Failed to load patient details:", err);
        setDetailsError("Could not load this patient's details.");
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoadingDetails(false);
      });

    return () => controller.abort();
  }, [patientId]);

  // Roster row fields stand in until the fuller record arrives, so the header
  // does not flash empty.
  const shown = details || rosterPatient || {};
  const name = displayPatientNameFromParts(shown, "UNKNOWN");
  const years = patientAgeYears(shown);
  const photo = normalizeBase64Image(shown.photo);
  const insurance = Array.isArray(details?.health_insurance)
    ? details.health_insurance.filter(Boolean)
    : [];

  return (
    <Box>
      <Box sx={{ mb: 2 }}>
        <Link
          component="button"
          type="button"
          onClick={onBack}
          underline="hover"
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            color: PR_COLORS.primary,
            fontWeight: 600,
            fontSize: "0.9rem",
          }}
        >
          <ArrowBackIcon fontSize="small" /> Back to list
        </Link>
      </Box>

      <Paper
        elevation={0}
        sx={{
          borderRadius: "12px",
          border: `1px solid ${PR_COLORS.border}`,
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            p: 2,
            background: "linear-gradient(135deg, #f5f7fa 0%, #e4ecf7 100%)",
          }}
        >
          <Avatar src={photo || undefined} sx={{ width: 64, height: 64, bgcolor: PR_COLORS.heading }}>
            <PersonIcon />
          </Avatar>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontWeight: 700, fontSize: "1.15rem", color: PR_COLORS.headingStrong }}>
              {name}
            </Typography>
            <Typography sx={{ color: PR_COLORS.subtle, fontSize: "0.88rem" }}>
              {[
                years !== "" ? `${years}y` : null,
                shown.gender || null,
                shown.patient_id,
              ]
                .filter(Boolean)
                .join(" · ")}
            </Typography>
          </Box>
        </Box>

        <Divider />

        {detailsError && (
          <Alert severity="error" sx={{ m: 2 }}>
            {detailsError}
          </Alert>
        )}

        {loadingDetails && !details && (
          <Box sx={{ p: 4, textAlign: "center" }}>
            <CircularProgress size={26} />
            <Typography sx={{ mt: 1.5, color: PR_COLORS.muted }}>
              Loading patient details…
            </Typography>
          </Box>
        )}

        {details && (
          <Box sx={{ p: 2 }}>
            <SectionTitle>Identifiers</SectionTitle>
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Field label="Patient ID" value={details.patient_id} />
              <Field label="ABHA Address" value={details.abha_id || NOT_PRESENT} />
              <Field label="ABHA Number" value={details.abha_no || NOT_PRESENT} />
            </Grid>

            <Divider sx={{ mb: 2 }} />

            <SectionTitle>Contact</SectionTitle>
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Field label="Phone" value={details.phone_no} />
              <Field label="Email" value={details.email} />
              <Field label="Address" value={joinAddress(details)} span={8} />
            </Grid>

            <Divider sx={{ mb: 2 }} />

            <SectionTitle>Personal</SectionTitle>
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Field label="Date of birth" value={details.dob} />
              <Field
                label="Age"
                value={years !== "" ? `${years} years` : details.age}
              />
              <Field label="Gender" value={details.gender} />
              <Field
                label="Height"
                value={details.height ? `${details.height} cm` : ""}
              />
              <Field
                label="Weight"
                value={details.weight ? `${details.weight} kg` : ""}
              />
            </Grid>

            <Divider sx={{ mb: 2 }} />

            <SectionTitle>Insurance</SectionTitle>
            <Box sx={{ mb: 1 }}>
              {insurance.length > 0 ? (
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                  {insurance.map((scheme) => (
                    <Chip key={scheme} label={scheme} size="small" />
                  ))}
                </Box>
              ) : (
                <Typography sx={{ fontSize: "0.92rem", color: PR_COLORS.muted }}>
                  No insurance scheme recorded
                </Typography>
              )}
            </Box>
          </Box>
        )}
      </Paper>

      <PastAppointmentsSection patientId={patientId} />

      <CareContextsSection patientId={patientId} />

      {/* Always rendered, never gated on the demographics fetch: a failed
          demographics read must not delete the retrieval section too. It only
          needs a patient_id, which the roster row already carries. */}
      <HealthRecordsAccordion patient={shown} />
    </Box>
  );
}
